import { Component, AfterViewInit, OnDestroy, ViewChild, ElementRef, Input, Output, EventEmitter, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import Quill from 'quill';
import { marked } from 'marked';
import TurndownService from 'turndown';

@Component({
  selector: 'app-markdown-editor',
  templateUrl: './markdown-editor.component.html',
  styleUrls: ['./markdown-editor.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MarkdownEditorComponent),
      multi: true
    }
  ]
})
export class MarkdownEditorComponent implements AfterViewInit, OnDestroy, ControlValueAccessor {
  @ViewChild('editor', { static: false }) editorElement!: ElementRef;

  @Input() placeholder = 'Start typing...';
  @Input() height = '300px';
  @Output() markdownChange = new EventEmitter<string>();

  markdown = '';
  private quillInstance: Quill | null = null;
  private turndownService: TurndownService;
  private isUpdatingFromMarkdown = false;
  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  constructor() {
    // Configure Turndown to preserve color styles
    this.turndownService = new TurndownService({
      headingStyle: 'atx',
      codeBlockStyle: 'fenced',
      emDelimiter: '*',
      strongDelimiter: '**',
      bulletListMarker: '-',
    });

    // Handle Quill's blockquote structure
    this.turndownService.addRule('quillBlockquote', {
      filter: (node: HTMLElement) => {
        return node.nodeName === 'BLOCKQUOTE' ||
          (node.classList && node.classList.contains('ql-blockquote'));
      },
      replacement: (content: string) => {
        // Remove extra newlines and format properly
        const lines = content.trim().split('\n');
        return lines.map(line => `> ${line}`).join('\n') + '\n\n';
      }
    });

    // Handle Quill's code-block structure
    this.turndownService.addRule('quillCodeBlock', {
      filter: (node: HTMLElement) => {
        return (node.nodeName === 'PRE' && node.classList.contains('ql-syntax')) ||
          (node.classList && node.classList.contains('ql-code-block'));
      },
      replacement: (content: string) => {
        // Quill wraps code blocks in <pre> with class ql-syntax
        return '\n```\n' + content.trim() + '\n```\n\n';
      }
    });

    // Handle ordered lists - remove extra spaces
    this.turndownService.addRule('orderedList', {
      filter: 'ol',
      replacement: (content: string, node: HTMLElement) => {
        let counter = 1;
        const lines = content.trim().split('\n');
        const result = lines.map(line => {
          if (line.trim()) {
            return `${counter++}. ${line.trim()}`;
          }
          return line;
        }).join('\n');
        return '\n' + result + '\n\n';
      }
    });

    // Handle unordered lists
    this.turndownService.addRule('unorderedList', {
      filter: 'ul',
      replacement: (content: string, node: HTMLElement) => {
        const lines = content.trim().split('\n');
        const result = lines.map(line => {
          if (line.trim()) {
            return `- ${line.trim()}`;
          }
          return line;
        }).join('\n');
        return '\n' + result + '\n\n';
      }
    });

    // Handle list items - strip Quill's UI elements
    this.turndownService.addRule('listItem', {
      filter: 'li',
      replacement: (content: string, node: HTMLElement) => {
        // Remove Quill's ql-ui span elements
        const cleanContent = content.replace(/<span class="ql-ui"[^>]*>.*?<\/span>/g, '').trim();
        return cleanContent + '\n';
      }
    });

    // Preserve span elements with style attributes for colors
    this.turndownService.addRule('preserveColoredText', {
      filter: (node: HTMLElement) => {
        return node.nodeName === 'SPAN' &&
          (node.style.color || node.style.backgroundColor);
      },
      replacement: (content: string, node: HTMLElement) => {
        const style = node.getAttribute('style');
        return `<span style="${style}">${content}</span>`;
      }
    });

    // Preserve underline (not standard in markdown)
    this.turndownService.addRule('underline', {
      filter: ['u'],
      replacement: (content: string) => {
        return `<u>${content}</u>`;
      }
    });
  }

  ngAfterViewInit(): void {
    this.initializeQuill();
  }

  ngOnDestroy(): void {
    if (this.quillInstance) {
      this.quillInstance.off('text-change');
      this.quillInstance = null;
    }
  }

  private initializeQuill(): void {
    if (!this.editorElement) {return;}

    this.quillInstance = new Quill(this.editorElement.nativeElement, {
      theme: 'snow',
      placeholder: this.placeholder,
      modules: {
        toolbar: [
          [{ header: [1, 2, 3, false] }],
          ['bold', 'italic', 'underline', 'strike'],
          [{ color: [] }, { background: [] }],
          ['blockquote', 'code-block'],
          [{ list: 'ordered'}, { list: 'bullet' }],
          ['link', 'image'],
          ['clean']
        ],
      }
    });

    const toolbar = this.quillInstance.getModule('toolbar');
    // @ts-ignore
    toolbar.addHandler('image', () => this.promptForImage(this.quillInstance));

    // Set initial content if markdown exists
    if (this.markdown) {
      this.updateQuillFromMarkdown(this.markdown);
    }

    // Listen for changes in Quill
    this.quillInstance.on('text-change', () => {
      if (!this.isUpdatingFromMarkdown && this.quillInstance) {
        const html = this.quillInstance.root.innerHTML;
        const newMarkdown = this.convertHtmlToMarkdown(html);
        this.markdownChange.emit(newMarkdown);
        this.onChange(newMarkdown);
      }
    });

    // Mark as touched on blur
    this.quillInstance.root.addEventListener('blur', () => {
      this.onTouched();
    });
  }

  private fixBlockquoteStructure(html: string): string {
    // Marked outputs: <blockquote>\n<p>content</p>\n</blockquote>
    // This regex ensures proper structure and removes extra whitespace
    return html.replace(/<blockquote>\s*<p>([\s\S]*?)<\/p>\s*<\/blockquote>/g,
      '<blockquote>$1</blockquote>');
  }

  private fixCodeBlockStructure(html: string): string {
    return html.replace(/<pre><code(?:\s+class="language-(\w+)")?>([\s\S]*?)<\/code><\/pre>/g,
      (match, language, content) => {
        // Keep HTML entities as-is for now, Quill will handle them
        // Use ql-code-block class which Quill recognizes
        return `<pre class="ql-code-block">${content}</pre>`;
      });
  }

  private fixListStructure(html: string): string {
    // Fix ordered lists - add data-list attribute to ALL <li> elements within <ol>
    html = html.replace(/<ol>([\s\S]*?)<\/ol>/g, (match, content) => {
      const fixedContent = content.replace(/<li>/g, '<li data-list="ordered">');
      return `<ol>${fixedContent}</ol>`;
    });

    // Fix unordered lists - add data-list attribute to ALL <li> elements within <ul>
    html = html.replace(/<ul>([\s\S]*?)<\/ul>/g, (match, content) => {
      const fixedContent = content.replace(/<li>/g, '<li data-list="bullet">');
      return `<ol>${fixedContent}</ol>`;
    });

    return html;
  }

  private updateQuillFromMarkdown(markdown: string): void {
    if (!this.quillInstance) {return;}

    this.isUpdatingFromMarkdown = true;

    let html = marked.parse(markdown, { async: false }) as string;

    // Fix blockquote structure for Quill
    html = this.fixBlockquoteStructure(html);

    // Fix code block structure for Quill
    html = this.fixCodeBlockStructure(html);

    // Fix list structure for Quill
    html = this.fixListStructure(html);

    const currentSelection = this.quillInstance.getSelection();

    this.quillInstance.root.innerHTML = html;

    // Try to restore cursor position
    if (currentSelection) {
      setTimeout(() => {
        try {
          this.quillInstance?.setSelection(currentSelection.index, currentSelection.length);
        } catch (e) {
          // Ignore if position no longer valid
        }
      }, 0);
    }

    this.isUpdatingFromMarkdown = false;
  }

  promptForImage(quillInstance: Quill) {
    const imageUrl = prompt('Enter image URL:');
    if (imageUrl && quillInstance) {
      const range = quillInstance.getSelection(true);
      quillInstance.insertEmbed(range.index, 'image', imageUrl);
      quillInstance.setSelection(range.index + 1, 0);
    }
  }

  private convertHtmlToMarkdown(html: string): string {
    // Clean up Quill's empty paragraphs and breaks
    const cleanHtml = html
      .replace(/<p><br><\/p>/g, '\n')
      .replace(/<p>\s*<\/p>/g, '\n')
      // Remove Quill's list UI spans
      .replace(/<span class="ql-ui"[^>]*><\/span>/g, '')
      // Convert Quill's bullet lists (ol with data-list="bullet") to ul
      .replace(/<ol>([\s\S]*?)<\/ol>/g, (match, content) => {
        // Check if this is a bullet list (has data-list="bullet")
        if (content.includes('data-list="bullet"')) {
          // Convert to ul and remove data-list attribute
          const cleanContent = content.replace(/data-list="bullet"/g, '');
          return `<ul>${cleanContent}</ul>`;
        }
        // It's an ordered list, just remove data-list="ordered" attribute
        const cleanContent = content.replace(/data-list="ordered"/g, '');
        return `<ol>${cleanContent}</ol>`;
      })
      // Handle Quill's code block structure specifically
      .replace(/<pre class="ql-syntax"[^>]*>([\s\S]*?)<\/pre>/g, (match, content) => {
        // Decode HTML entities in code blocks
        const decoded = content
          .replace(/&lt;/g, '<')
          .replace(/&gt;/g, '>')
          .replace(/&amp;/g, '&')
          .replace(/&quot;/g, '"')
          .replace(/<br>/g, '\n');
        return `<pre class="ql-syntax">${decoded}</pre>`;
      });
    return this.turndownService.turndown(cleanHtml).trim();
  }

  // ControlValueAccessor implementation
  writeValue(value: string): void {
    this.markdown = value || '';
    if (this.quillInstance) {
      this.updateQuillFromMarkdown(this.markdown);
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (this.quillInstance) {
      this.quillInstance.enable(!isDisabled);
    }
  }
}
